import React from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {Alert, Link} from '@forecast-it/design-system';

/** Where the Xero items, accounts and tracking categories are imported. */
export const XERO_CONFIGURATION_PATH = '/admin/integrations/xero';

type XeroNothingImportedAlertProps = {
	/** Whether the user may open the Xero configuration page, which is where the lists are imported. */
	canOpenConfiguration: boolean;
	onOpenConfiguration: () => void;
};

/**
 * The warning the Export to Xero window shows when the organisation has imported no items, no
 * accounts and no tracking categories. What it asks the user to do depends on whether they can
 * import them: a link to the Xero configuration for those who can, "ask your admin" for the rest.
 *
 * Both sentences are one paragraph, the second right after the first.
 */
const XeroNothingImportedAlert = ({canOpenConfiguration, onOpenConfiguration}: XeroNothingImportedAlertProps): JSX.Element => {
	const {formatMessage} = useIntl();

	const message = (
		<>
			{formatMessage({id: 'integrations.xero.export.nothing_imported'})}{' '}
			{canOpenConfiguration ? (
				<FormattedMessage
					id={'integrations.xero.export.nothing_imported.go_to_configuration'}
					values={{
						a: (...chunks: React.ReactNode[]) => (
							<Link
								href={XERO_CONFIGURATION_PATH}
								onClick={(event: React.MouseEvent) => {
									// A modified or non-primary click opens a new tab or window, which the browser does.
									if (
										event.metaKey ||
										event.ctrlKey ||
										event.shiftKey ||
										event.altKey ||
										event.button !== 0
									) {
										return;
									}
									// The window closes first and the app navigates, rather than the browser.
									event.preventDefault();
									onOpenConfiguration();
								}}
							>
								{chunks}
							</Link>
						),
					}}
				/>
			) : (
				formatMessage({id: 'integrations.xero.export.nothing_imported.ask_admin'})
			)}
		</>
	);

	/*
	 * The Alert types its text as a string but renders it as the content of its own Text, so a
	 * message with a link in it flows as one paragraph. Anything passed as its children would be
	 * pushed to the far right of the Alert instead, apart from the first sentence.
	 */
	return <Alert type={Alert.TYPE.WARNING} text={message as unknown as string} />;
};

export default XeroNothingImportedAlert;
